import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router';
import {
    showSortingMenu,
    resetSortingMenu,
    sortingEnabledOptions,
    handleDataForSidebar,
    getModalSwitch,
    removeDetailsBreadcrumb,
    setExceptionHandler,
    customBreadcrumbs
} from '@app/store/actions';
import {
    getMenuItemById,
    passwordFilter,
    folderFilter,
    useDebounce,
    searchMinimumLength,
    hasOrgPermission,
    genericErrorToast,
    IndividualVaultType,
    getActiveVaultPermission,
    resolveOrgName
} from '@app/helpers';
import { IListPageBarEnabled, IVaultDetail, TranslationsTypes, ISelectIdentities, IVaultPasswordPolicyDTO } from '@app/models';
import {
    PasslySelect,
    ModalComponent,
    IModalData,
    mainMenuTypes,
    IMenuItem,
    PasslyButton,
    PasslyTextField
} from '@app/components';
import { PasslyTabs, PasslyTabContent, PasslyTab } from '@app/componentsV2';
import { PasswordDetailsDialog } from '@app/pages/password/dialogs/passwordDetails.dialog';
import FolderListComponent from '../components/folderList.component';
import PasswordListComponent from '../components/passwordList.component';
import VaultInformation from '../components/vaultInformation.component';
import VaultAccess from '../components/vaultAccess.component';
import { vaultsService, vaultPasswordPolicyService, basicService } from '@app/services';
import { parse } from 'qs';
import { Grid, Box, Typography, TableCell } from '@material-ui/core';
import { ManageVault, vaultAdmin, vaultAdd, vaultRead, vaultLaunch } from '@app/store/constants';
import SwipeableViews from 'react-swipeable-views';
import { Add } from '@material-ui/icons';
import { useTypedTranslation } from '@app/hooks';
import SelectIdentitiesV2 from '@app/componentsV2/Modal/selectIdentitiesV2.component';

type SearchType = { value: string; label: TranslationsTypes };

enum TableHeadEnum {
    name = 'Name',
    organizationId = 'OrganizationId',
    peopleCount = 'People@odata.count'
}

const VaultDetailsPage = () => {
    const { id: vaultId } = useParams<{ id: string }>();
    const location = useLocation();
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const orgs = useSelector((state: any) => state.basic.shellConfig.organizations);
    const queryParams = parse(location.search, { ignoreQueryPrefix: true });
    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.VaultManager);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const userVaultsPermissions = useSelector((state: any) => state.signIn.currentUser.vaultPermissions);
    const permissions = userVaultsPermissions ? getActiveVaultPermission(userVaultsPermissions[vaultId]) : new Set<string>();

    const manageOnly = !(permissions?.has(vaultRead) || permissions?.has(vaultLaunch)) && hasOrgPermission([ManageVault]);
    const [menuItem] = useState<IMenuItem | undefined>(item);
    const [selectedFolder, setSelectedFolder] = useState('');
    const [querySharedLink, setQuerySharedLink] = useState(queryParams.shared !== undefined);
    const [queryFolderId, setQueryFolderId] = useState(queryParams.folderId !== undefined ? queryParams.folderId : '');
    const [queryFolderName] = useState(queryParams.folderName !== undefined ? queryParams.folderName : '');
    const [queryPasswordId] = useState(queryParams.passwordId !== undefined ? queryParams.passwordId : '');
    const [queryPasswordName] = useState(queryParams.passName !== undefined ? queryParams.passName : '');
    const [vaultName, setVaultName] = useState('');
    const [searchField, setSearchField] = useState('');
    const [searchType, setSearchType] = useState<string>(folderFilter);
    const [showAddGroup, setShowAddGroup] = useState(false);
    const [foldersLoading, setFoldersLoading] = useState(true);
    const [tabIndex, setTabIndex] = React.useState(manageOnly ? 1 : 0);
    const [vault, setVault] = useState<IVaultDetail>({} as IVaultDetail);
    const [passwordPolicies, setPasswordPolicies] = useState<any>(null);
    const [groupsToAdd, setGroupsToAdd] = useState<string[]>([]);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });

    // because of timing with debounced values and child components, set he
    // actual searchable value here
    const [searchableValue, setSearchableValue] = useState('');

    const debouncedSearch = useDebounce(searchField.trim(), 500);

    const searchTypes: SearchType[] = [
        { value: folderFilter, label: 'VAULT_FOLDER_TAB' }
    ];

    useEffect(() => {
        dispatch(setExceptionHandler(true));
        dispatch(resetSortingMenu());
        dispatch(showSortingMenu());
        dispatch(sortingEnabledOptions(setListPageBarEnabledOptions));
        dispatch(handleDataForSidebar(menuItem));

        vaultsService.getVault(vaultId, genericErrorToast, handleVaultSuccess);
        vaultPasswordPolicyService.getAllVaultPasswordPolicies().then(handleVaultPasswordPolicySuccess).catch(genericErrorToast);

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    useEffect(() => {
        if (debouncedSearch !== '' && debouncedSearch.length >= searchMinimumLength) {
            setQueryFolderId('');
            setSearchableValue(debouncedSearch);
        } else if (debouncedSearch === '' && searchableValue !== '') {
            setSearchableValue(debouncedSearch);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        if (vaultName !== '') {
            dispatch(
                customBreadcrumbs([
                    {
                        name: translation('PASSWORD_SERVER')
                    },
                    {
                        name: translation('APP_VAULT_MANAGER'),
                        link: '/passwordserver/vaults'
                    },
                    {
                        name: vaultName,
                        link: `/passwordserver/vaults/${vaultId}`
                    }
                ])
            );
        }
    }, [vaultName]);

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: false
    };

    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        permissions?.has(vaultRead) || permissions?.has(vaultLaunch) ? setTabIndex(newValue) : setTabIndex(newValue + 1);
    };

    const handleChangeIndex = (index: number) => {
        permissions?.has(vaultRead) || permissions?.has(vaultLaunch) ? setTabIndex(index) : setTabIndex(index + 1);
    };

    const handleVaultSuccess = (response: any) => {
        setVault(response.data);
        setVaultName(response.data.DisplayName);
    };

    const handleVaultPasswordPolicySuccess = (values: IVaultPasswordPolicyDTO[]) => {
        const resultValues = values.map((value) => {
            return { name: value.DisplayName, code: value.Id };
        });
        setPasswordPolicies(resultValues);
    };

    const onFolderClick = (folderId: string) => {
        setSelectedFolder(folderId);
    };

    const openPasswordDetailsModal = (passwordId: string, vaultId: string, passwordName: string, revealPassword: boolean = false) => {
        if (revealPassword) {
            // We've opened the password, now prevent it from a second time
            setQuerySharedLink(false);
        }
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: passwordName,
            modalSwitcher: modalSwitchState,
            form: <PasswordDetailsDialog passwordId={passwordId} vaultId={vaultId} revealPassword={revealPassword} />
        });
    };

    const addGroupModalCallback = (ids: string[]) => {
        setShowAddGroup(false);
        setGroupsToAdd(ids);
    };

    const getComplexOrganizationCell = (row: any) => {
        return <TableCell>{resolveOrgName(row.OrganizationId, orgs)}</TableCell>;
    };

    const identityModalData: ISelectIdentities[] = [
        {
            fetchApiUrl: `api/directory/Vaults(${vaultId})/GroupsNotInVault`,
            dialogHeader: translation('VAULT_ADD_GROUPS'),
            selectedLabel: translation('GROUPS'),
            emptyResults: translation('VAULT_NO_GROUPS_TO_ADD'),
            primaryButtonLabel: translation('VAULT_ADD'),
            primaryButtonCallback: addGroupModalCallback,
            columns: [
                {
                    id: TableHeadEnum.name,
                    label: 'GROUPS_NAME',
                    type: 'string',
                    searchable: true
                },
                {
                    id: TableHeadEnum.organizationId,
                    label: 'ORGANIZATION',
                    type: 'getComplexCell',
                    getComplexCell: getComplexOrganizationCell,
                    displayValueFromOrg: true
                },
                {
                    id: TableHeadEnum.peopleCount,
                    label: 'USERS',
                    type: 'string',
                    hideSorting: true,
                    countable: true,
                    expandAttribute: 'People'
                }
            ]
        }
    ];

    const setShow = () => {
        setShowAddGroup(true);
    };

    const getPasswordView = () => {
        return (
            <div>
                <div className='vaults__table-body'>
                    <FolderListComponent
                        vaultId={vaultId}
                        folderClickHandler={onFolderClick}
                        queryFolderId={String(queryFolderId)}
                        queryFolderName={String(queryFolderName)}
                        filterString={searchableValue}
                        searchType={searchType}
                        foldersLoadingCallback={setFoldersLoading}
                        permissions={permissions}
                    />
                    <PasswordListComponent
                        permissions={permissions}
                        vaultId={vaultId}
                        isIndividual={vault.VaultType === IndividualVaultType}
                        vaultName={vaultName}
                        folderId={selectedFolder}
                        queryPasswordId={String(queryPasswordId)}
                        queryPasswordName={String(queryPasswordName)}
                        querySharedLink={querySharedLink}
                        openPasswordCallback={openPasswordDetailsModal}
                        foldersLoading={foldersLoading}
                    />
                </div>
            </div>
        );
    };

    return (
        <>
            <ModalComponent {...modalData} />
            {showAddGroup && <SelectIdentitiesV2 open={showAddGroup} closeCallback={setShowAddGroup} tabs={identityModalData} fixedFooter={true} />}
            <div className='passwordVaults'>
                <Grid direction='row' container wrap='nowrap'>
                    <Grid container direction='column' justifyContent='flex-start' alignItems='stretch' wrap='nowrap'>
                        <Grid item>
                            <Box m={2}>
                                <Typography variant='h1'>{vaultName}</Typography>
                            </Box>
                        </Grid>

                        {(permissions?.has(vaultAdmin) || hasOrgPermission([ManageVault])) && (
                            <Grid item lg={9}>
                                <Box ml={2} mb={2}>
                                    <PasslyTabs
                                        value={permissions?.has(vaultRead) || permissions?.has(vaultLaunch) ? tabIndex : tabIndex - 1}
                                        onChange={handleChange}
                                    >
                                        {(permissions?.has(vaultRead) || permissions?.has(vaultLaunch)) && (
                                            <PasslyTab label={translation('VAULT_PASSWORDS_TAB')} />
                                        )}
                                        <PasslyTab label={translation('VAULT_INFO_TAB')} />
                                        {vault.VaultType !== IndividualVaultType && <PasslyTab label={translation('VAULT_ACCESS')} />}
                                    </PasslyTabs>
                                </Box>
                            </Grid>
                        )}
                        {tabIndex === 0 && (permissions?.has(vaultRead) || permissions?.has(vaultLaunch)) && (
                            <Grid item lg={9}>
                                <div className='vaults__section-head-inputs vaults__section-head-inputs-edit'>
                                    <PasslyTextField
                                        name='search'
                                        label=''
                                        value={searchField}
                                        onChange={(e: any) => setSearchField(e.target.value)}
                                        id='search-input'
                                        inputProps={{ maxLength: 255 }}
                                        placeholder={translation('SEARCH')}
                                        showSearchIcon={true}
                                        />
                                    <PasslySelect
                                        name='searchTypeSelected'
                                        onChange={(e: any) => setSearchType(e.target.value)}
                                        id='searchType-input'
                                        value={searchType}
                                        options={searchTypes}
                                    />
                                </div>
                            </Grid>
                        )}
                    </Grid>
                    <Grid container alignItems='flex-end' justifyContent='flex-end' direction='column'>
                        {tabIndex === 0 && permissions?.has(vaultAdd) && (
                            <Grid item>
                                <Box m={2}>
                                    <PasslyButton
                                        color='primary'
                                        variant='contained'
                                        onClick={() =>
                                            basicService.redirect(
                                                `/passwordserver/vaults/${vaultId}/addpassword?vaultName=${encodeURIComponent(
                                                    vaultName
                                                )}&folderId=${selectedFolder}&isIndividual=${vault.VaultType === IndividualVaultType}`
                                            )
                                        }
                                    >
                                        <Add color='inherit' />
                                        {translation('NEW_PASSWORD')}
                                    </PasslyButton>
                                </Box>
                            </Grid>
                        )}
                        {tabIndex === 2 && (permissions?.has(vaultAdmin) || hasOrgPermission([ManageVault]) || manageOnly) && (
                            <Grid item>
                                <Box m={2}>
                                    <PasslyButton color='primary' variant='contained' onClick={setShow}>
                                        <Add color='inherit' />
                                        {translation('ADD_GROUP')}
                                    </PasslyButton>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {permissions?.has(vaultAdmin) || hasOrgPermission([ManageVault]) ? (
                    <SwipeableViews index={tabIndex} onChangeIndex={handleChangeIndex}>
                        <PasslyTabContent value={tabIndex} index={0}>
                            {(permissions?.has(vaultRead) || permissions?.has(vaultLaunch)) && getPasswordView()}
                        </PasslyTabContent>

                        <PasslyTabContent value={tabIndex} index={1}>
                            <VaultInformation
                                vault={vault}
                                passwordPolicies={passwordPolicies}
                                isIndividual={vault.VaultType === IndividualVaultType}
                                setVaultName={setVaultName}
                            />
                        </PasslyTabContent>

                        {vault.VaultType && vault.VaultType !== IndividualVaultType && (
                            <PasslyTabContent value={tabIndex} index={2}>
                                <VaultAccess vaultId={vaultId} groupsToAdd={groupsToAdd} />
                            </PasslyTabContent>
                        )}
                    </SwipeableViews>
                ) : (
                    getPasswordView()
                )}
            </div>
        </>
    );
};

export default VaultDetailsPage;
